import { useState, useEffect, useMemo } from 'react';
import { isSameDay, format } from 'date-fns';
import { Plus, Lock, Unlock } from 'lucide-react';
import type { AppointmentListItem, DoctorAvailabilityException } from '@/api';
import { AppointmentGridCard } from './AppointmentGridCard';
import { cn } from '@/utils/cn';

export interface DayColumnProps {
  date: Date;
  isToday: boolean;
  appointments: AppointmentListItem[];
  exceptions: DoctorAvailabilityException[];
  startHour?: number;
  endHour?: number;
  hourHeight?: number;
  onSelectAppointment: (appointment: AppointmentListItem) => void;
  onBlockSlot?: (date: Date, startTime: string, endTime: string) => void;
  onUnblockSlot?: (exceptionId: string) => void;
}

export function DayColumn({
  date,
  isToday,
  appointments,
  exceptions,
  startHour = 7,
  endHour = 22,
  hourHeight = 72,
  onSelectAppointment,
  onBlockSlot,
  onUnblockSlot,
}: DayColumnProps) {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Atualiza o relógio a cada minuto para o marcador em tempo real
  useEffect(() => {
    if (!isToday) return;
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, [isToday]);

  const totalHours = endHour - startHour;
  const columnHeight = totalHours * hourHeight;
  const dateStr = format(date, 'yyyy-MM-dd');

  // Filtra consultas do dia
  const dayAppointments = useMemo(() => {
    return appointments.filter((apt) => {
      const aptDate = new Date(apt.scheduledAt);
      return isSameDay(aptDate, date);
    });
  }, [appointments, date]);

  // Filtra exceções (bloqueios) do dia
  const dayExceptions = useMemo(() => {
    return exceptions.filter((exc) => {
      const parsedDate = new Date(exc.date);
      const excDateStr = format(parsedDate, 'yyyy-MM-dd');
      return excDateStr === dateStr && exc.isBlocked;
    });
  }, [exceptions, dateStr]);

  // Exceção de dia inteiro (startTime nulo)
  const fullDayException = dayExceptions.find((exc) => !exc.startTime);

  // Calcula posição da linha do horário atual
  const currentTimePosition = useMemo(() => {
    if (!isToday) return null;
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const startMinutes = startHour * 60;
    const endMinutes = endHour * 60;

    if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
      return null;
    }

    return ((currentMinutes - startMinutes) / 60) * hourHeight;
  }, [isToday, currentTime, startHour, endHour, hourHeight]);

  // Cria os blocos de 30 minutos para guias e detecção de hover para bloqueio
  const halfHourSlots = useMemo(() => {
    const slots: Array<{
      hour: number;
      minute: number;
      timeStr: string;
      endTimeStr: string;
      top: number;
    }> = [];

    for (let h = startHour; h < endHour; h++) {
      slots.push({
        hour: h,
        minute: 0,
        timeStr: `${String(h).padStart(2, '0')}:00`,
        endTimeStr: `${String(h).padStart(2, '0')}:30`,
        top: (h - startHour) * hourHeight,
      });
      slots.push({
        hour: h,
        minute: 30,
        timeStr: `${String(h).padStart(2, '0')}:30`,
        endTimeStr: `${String(h + 1).padStart(2, '0')}:00`,
        top: (h - startHour) * hourHeight + hourHeight / 2,
      });
    }

    return slots;
  }, [startHour, endHour, hourHeight]);

  // Converte "HH:mm" para minutos a partir de startHour
  const timeToOffset = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    const minutes = (h ?? 0) * 60 + (m ?? 0) - startHour * 60;
    return (minutes / 60) * hourHeight;
  };

  return (
    <div
      className={cn(
        'relative border-r border-border/60 bg-background transition-colors',
        isToday && 'bg-primary/[0.02]'
      )}
      style={{ height: columnHeight }}
    >
      {/* Grade de fundo: linhas de horas e meias horas */}
      {halfHourSlots.map((slot) => (
        <div
          key={`${slot.hour}-${slot.minute}`}
          style={{ top: `${slot.top}px`, height: `${hourHeight / 2}px` }}
          className={cn(
            'group absolute left-0 right-0 border-b transition-colors select-none',
            slot.minute === 0
              ? 'border-border/60 border-t-0'
              : 'border-border/25 border-dashed',
            'hover:bg-muted/30'
          )}
        >
          {/* Ação rápida para bloquear no hover se o médico tiver permissão */}
          {onBlockSlot && !fullDayException && (
            <button
              type="button"
              onClick={() => onBlockSlot(date, slot.timeStr, slot.endTimeStr)}
              className="absolute inset-y-1 right-2 hidden group-hover:flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground bg-background/80 hover:bg-background px-1.5 py-0.5 rounded shadow-xs border border-border transition-all z-10"
              title={`Bloquear horário das ${slot.timeStr} às ${slot.endTimeStr}`}
            >
              <Plus className="h-3 w-3" />
              <span>Bloquear {slot.timeStr}</span>
            </button>
          )}
        </div>
      ))}

      {/* Banner / Overlay se o dia inteiro estiver bloqueado */}
      {fullDayException && (
        <div
          className="absolute inset-0 bg-muted/60 backdrop-blur-[1px] z-20 flex flex-col items-center justify-start p-3 text-center border-dashed border-2 border-muted-foreground/30 m-1 rounded-lg"
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mt-4">
            <Lock className="h-4 w-4 text-amber-500" />
            <span>Dia Bloqueado</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-[90%]">
            {fullDayException.reason || 'Folga ou indisponibilidade configurada'}
          </p>
          {onUnblockSlot && (
            <button
              type="button"
              onClick={() => onUnblockSlot(fullDayException.id)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 bg-background/90 px-2.5 py-1 rounded-md border border-border shadow-xs hover:bg-destructive/10 transition-colors"
            >
              <Unlock className="h-3.5 w-3.5" />
              <span>Desbloquear Dia</span>
            </button>
          )}
        </div>
      )}

      {/* Exceções de horários específicos (bloqueios parciais) */}
      {!fullDayException &&
        dayExceptions
          .filter((exc) => exc.startTime && exc.endTime)
          .map((exc) => {
            const top = Math.max(0, timeToOffset(exc.startTime!));
            const endOffset = timeToOffset(exc.endTime!);
            const height = Math.max(endOffset - top, 26);

            return (
              <div
                key={exc.id}
                style={{ top: `${top}px`, height: `${height}px` }}
                className="absolute left-1 right-1 rounded-md bg-muted/90 border border-dashed border-muted-foreground/40 p-1.5 z-15 flex items-center justify-between overflow-hidden shadow-xs"
                title={`Bloqueado: ${exc.startTime} - ${exc.endTime} (${exc.reason || 'Sem motivo'})`}
              >
                <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                  <Lock className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="min-w-0">
                    <span className="text-[10px] font-semibold block leading-tight truncate">
                      {exc.startTime} - {exc.endTime}
                    </span>
                    <span className="text-[9px] text-muted-foreground/80 block leading-tight truncate">
                      {exc.reason || 'Bloqueado'}
                    </span>
                  </div>
                </div>

                {onUnblockSlot && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnblockSlot(exc.id);
                    }}
                    className="shrink-0 p-1 text-muted-foreground hover:text-destructive hover:bg-background/80 rounded transition-colors"
                    title="Remover bloqueio deste horário"
                  >
                    <Unlock className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}

      {/* Consultas do dia */}
      {!fullDayException &&
        dayAppointments.map((appointment) => (
          <AppointmentGridCard
            key={appointment.id}
            appointment={appointment}
            startHour={startHour}
            hourHeight={hourHeight}
            onClick={onSelectAppointment}
          />
        ))}

      {/* Marcador do Horário Atual (Current Time Indicator) */}
      {isToday && currentTimePosition !== null && (
        <div
          style={{ top: `${currentTimePosition}px` }}
          className="absolute left-0 right-0 z-30 pointer-events-none flex items-center"
        >
          <div className="h-2.5 w-2.5 rounded-full bg-rose-500 -ml-1.25 shadow-sm ring-2 ring-background" />
          <div className="h-[2px] flex-1 bg-rose-500 shadow-xs" />
        </div>
      )}
    </div>
  );
}
